import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { getModelDetail, clearState } from '../../redux/actions/models/detail';
import { editModelRequest } from '../../redux/actions/models/edit';
import { deleteModelRequest, deleteModelClear } from '../../redux/actions/models/delete';
import {
  fetchRequest as runTrainingRequest,
  clearState as runTrainingClearState,
} from '../../redux/actions/models/runTraining';
import {
  fetchRequest as promotionDecisionRequest,
  clearState as promotionDecisionClearState,
} from '../../redux/actions/models/promotionDecision';
import {
  Box, Typography, Grid, CircularProgress,
  CardContent, Button, Alert, Tooltip, Divider,
  Dialog, DialogTitle, DialogContent, Chip, Stack,
  DialogContentText, DialogActions,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { PipelinesIcon } from '../global/CustomIcons';
import ErrorIcon from '@mui/icons-material/Error';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useTheme, alpha } from '@mui/material/styles';
import { format } from '../../utils/datetime';
import StaticCard from '../common/StaticCard';
import AccuracyInfoTooltip from '../common/AccuracyInfoTooltip';
import TrainingsTable from './TrainingsTable';
import PageBreadcrumbs from '../common/PageBreadcrumbs';
import useRealtimeRefresh, { eventPrefix } from '../../realtime/useRealtimeRefresh';
import { formatCredits } from '../billing/billingHelpers';

const ALL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_LETTERS = { Monday: 'M', Tuesday: 'T', Wednesday: 'W', Thursday: 'T', Friday: 'F', Saturday: 'S', Sunday: 'S' };

const formatFrequency = (seconds) => {
  const n = parseInt(seconds, 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n % 86400 === 0) {
    const d = n / 86400;
    return d === 1 ? 'Every day' : `Every ${d} days`;
  }
  if (n % 3600 === 0) {
    const h = n / 3600;
    return h === 1 ? 'Every hour' : `Every ${h} hours`;
  }
  if (n % 60 === 0) {
    const m = n / 60;
    return m === 1 ? 'Every minute' : `Every ${m} minutes`;
  }
  return `Every ${n} seconds`;
};

const describeDays = (days) => {
  if (!Array.isArray(days) || days.length === 0) return null;
  if (days.length === 7) return 'every day';
  const wd = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const we = ['Saturday', 'Sunday'];
  const setEq = (a, b) => a.length === b.length && a.every((x) => b.includes(x));
  if (setEq(days, wd)) return 'weekdays';
  if (setEq(days, we)) return 'weekends';
  if (days.length <= 3) return days.map((d) => d.slice(0, 3)).join(', ');
  return `${days.length} days/week`;
};

const describeWindow = (s) => {
  const start = s?.start_time || '00:00:00';
  const end = s?.end_time || '23:59:59';
  const fullDay = (start === '00:00:00') && (end === '23:59:59' || end === '23:59');
  if (fullDay) return null;
  return `${start.slice(0, 5)}–${end.slice(0, 5)}`;
};

const hasSchedule = (model) => {
  const s = model?.training_schedule;
  if (!s) return false;
  const freq = parseInt(s.frequency, 10);
  if (Number.isFinite(freq) && freq > 0) return true;
  if (Array.isArray(s.days) && s.days.length > 0) return true;
  return false;
};

// Compact inline schedule summary used inside the metrics card.
const ScheduleSummary = ({ model }) => {
  const theme = useTheme();
  const schedule = model?.training_schedule || {};
  const days = Array.isArray(schedule.days) ? schedule.days : [];
  const freq = formatFrequency(schedule.frequency);
  const dayDesc = describeDays(days);
  const window = describeWindow(schedule);
  const tz = schedule.timezone || 'UTC';

  // Empty days = the scheduler never fires. That's either a data-driven
  // pipeline (new-data trigger enabled) or a run-once / manual one.
  if (days.length === 0) {
    const dataTrigger = model?.retrain_triggers?.on_new_data;
    if (dataTrigger?.enabled) {
      const minRows = dataTrigger.min_rows || 100;
      return (
        <Box>
          <Typography variant="caption" color="text.secondary">Trigger</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            On new data
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
              — retrains after {minRows.toLocaleString()} new rows land in the segment
            </Typography>
          </Typography>
        </Box>
      );
    }
    return (
      <Box>
        <Typography variant="caption" color="text.secondary">Trigger</Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          Manual
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
            — no recurring schedule; runs when you hit "Run training"
          </Typography>
        </Typography>
      </Box>
    );
  }

  const summaryParts = [];
  if (freq) summaryParts.push(freq);
  if (dayDesc) summaryParts.push(dayDesc);
  if (window) summaryParts.push(window);
  const summary = summaryParts.length ? summaryParts.join(' · ') : 'Not scheduled';

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">Schedule</Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3, mb: 0.75 }}>
        {summary}
        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
          ({tz})
        </Typography>
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {ALL_DAYS.map((day) => {
          const on = days.includes(day);
          const isWeekend = day === 'Saturday' || day === 'Sunday';
          return (
            <Tooltip key={day} title={day} arrow>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  border: '1px solid',
                  borderColor: on
                    ? (theme.palette.schedule?.daySelected || theme.palette.primary.main)
                    : theme.palette.divider,
                  backgroundColor: on
                    ? (theme.palette.schedule?.daySelected || theme.palette.primary.main)
                    : 'transparent',
                  color: on
                    ? (theme.palette.schedule?.dayText || theme.palette.primary.contrastText)
                    : (isWeekend ? theme.palette.text.secondary : theme.palette.text.primary),
                  fontSize: '0.7rem',
                  fontWeight: 600,
                }}
              >
                {DAY_LETTERS[day]}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

// Tiny status indicator (colored dot + label) so the chips no longer
// take up valuable real-estate inside the metrics card.
const StatusDot = ({ color, label, pulsing = false }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: color,
        animation: pulsing ? 'pulse 1.6s ease-in-out infinite' : 'none',
        '@keyframes pulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.35 },
        },
      }}
    />
    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      {label}
    </Typography>
  </Box>
);

const ModelDetailPage = ({
  getModelDetail,
  clearState,
  model,
  isLoading,
  error,
  deleteModel,
  deleteError,
  deleteResponse,
  deleteModelClear,
  runTraining,
  runTrainingResponse,
  runTrainingError,
  runTrainingClear,
  promotionDecide,
  promotionDecisionResponse,
  promotionDecisionError,
  promotionDeciding,
  promotionDecisionClear,
}) => {
  const { modelId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const [workspaceSelected, setWorkspaceSelected] = useState(true);
  const [isTogglingActiveState, setIsTogglingActiveState] = useState(false);
  const [isRunningTraining, setIsRunningTraining] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(false);
  const [actionSuccessMessage, setActionSuccessMessage] = useState('');
  const [actionError, setActionError] = useState(null);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const workspaceId = localStorage.getItem('WORKSPACE_ID');
    if (!workspaceId) {
      setWorkspaceSelected(false);
      return;
    }

    if (modelId) {
      getModelDetail(modelId, { trainings_page: 0, trainings_per_page: 20 });
    }

    return () => {
      clearState();
    };
  }, [getModelDetail, clearState, modelId]);

  // Live (no polling): refresh the pipeline header (in-training dot, best
  // score, total cost) when a training/deployment transition lands. The
  // Trainings table refreshes itself off the same events.
  useRealtimeRefresh(
    eventPrefix('training.', 'deployment.'),
    () => { if (modelId) dispatch(getModelDetail(modelId, { trainings_page: 0, trainings_per_page: 20 })); },
  );

  useEffect(() => {
    if (deleteResponse) {
      setTimeout(() => {
        navigate('/pipelines');
        deleteModelClear();
      }, 1000);
    }
  }, [deleteResponse, navigate, deleteModelClear]);

  useEffect(() => {
    if (deleteError) {
      setIsDeleting(false);
      setActionError(deleteError);
      setIsDeleteDialogOpen(false);
    }
  }, [deleteError]);

  const handleBack = () => {
    navigate('/pipelines');
  };

  const handleGoToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleToggleTraining = () => {
    if (!model || isTogglingActiveState) return;

    const newActiveState = !model.active;

    setIsTogglingActiveState(true);
    setActionSuccess(false);
    setActionError(null);

    const updatePayload = { active: newActiveState };

    try {
      dispatch(editModelRequest(modelId, updatePayload));

      setTimeout(() => {
        setIsTogglingActiveState(false);
        setActionSuccess(true);
        setActionSuccessMessage(newActiveState
          ? "Pipeline resumed successfully!"
          : "Pipeline paused successfully!"
        );
      }, 500);
    } catch (err) {
      setActionError(`Failed to ${newActiveState ? 'resume' : 'pause'} training`);
      setIsTogglingActiveState(false);
    }
  };

  const handleRunTraining = () => {
    if (!model || isRunningTraining || model.in_training) return;

    setIsRunningTraining(true);
    setActionSuccess(false);
    setActionError(null);
    runTraining({ modelId });
  };

  // The saga refreshes the model detail 1200ms after the queue call
  // succeeds; keep the button spinner running on the same cadence.
  useEffect(() => {
    if (!runTrainingResponse) return undefined;
    setActionSuccess(true);
    setActionSuccessMessage('Training queued.');
    const timer = setTimeout(() => setIsRunningTraining(false), 1200);
    return () => clearTimeout(timer);
  }, [runTrainingResponse]);

  useEffect(() => {
    if (!runTrainingError) return;
    setActionError(runTrainingError);
    setIsRunningTraining(false);
  }, [runTrainingError]);

  // Resolve the pending manual-promotion decision (created by the
  // pipeline automation sweep when a new training beats the serving
  // version and the promotion policy is "ask me first").
  const handlePromotionDecision = (action) => {
    if (promotionDeciding) return;
    setActionSuccess(false);
    setActionError(null);
    promotionDecide({ modelId, decision: action });
  };

  useEffect(() => {
    if (!promotionDecisionResponse) return;
    setActionSuccess(true);
    setActionSuccessMessage(
      promotionDecisionResponse.result?.message
      || (promotionDecisionResponse.decision === 'promote'
        ? 'New version promoted; the previous deployment is being retired.'
        : 'Kept the current version.'),
    );
  }, [promotionDecisionResponse]);

  useEffect(() => {
    if (promotionDecisionError) setActionError(promotionDecisionError);
  }, [promotionDecisionError]);

  // Reset the action slices on unmount so a later visit doesn't replay
  // a stale success/error transition.
  useEffect(() => () => {
    runTrainingClear();
    promotionDecisionClear();
  }, [runTrainingClear, promotionDecisionClear]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      if (typeof dateString === 'object' && dateString.$date) {
        dateString = dateString.$date;
      }
      if (dateString instanceof Date) {
        return format(dateString, 'PPpp');
      }
      return format(new Date(dateString), 'PPpp');
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const handleOpenDeleteDialog = () => setIsDeleteDialogOpen(true);
  const handleCloseDeleteDialog = () => setIsDeleteDialogOpen(false);
  const handleConfirmDelete = () => {
    setIsDeleting(true);
    deleteModel(modelId);
  };

  if (!workspaceSelected) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <ErrorIcon color="warning" style={{ fontSize: 60, marginBottom: 16 }} />
        <Typography variant="h5" gutterBottom>No workspace selected</Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          You need to select a workspace before viewing pipeline details.
        </Typography>
        <Alert severity="info" sx={{ mb: 3, maxWidth: 500, margin: '0 auto' }}>
          Please select a workspace from the dropdown in the header or go to the workspaces page to create a new one.
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGoToWorkspaces}
          sx={{ mt: 2 }}
        >
          Go to workspaces
        </Button>
      </Box>
    );
  }

  // Only blank to a full-page spinner on the INITIAL load (no model yet).
  // Background refetches (realtime events, pause/resume) keep the current
  // content on screen so the page doesn't flash/reload.
  if (isLoading && !model) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <ErrorIcon color="error" style={{ fontSize: 60, marginBottom: 16 }} />
        <Typography variant="h5" color="error" gutterBottom>Error Loading Pipeline Details</Typography>
        <Typography variant="body1" paragraph>{error}</Typography>
        <Alert severity="warning" sx={{ mb: 3, maxWidth: 500, margin: '0 auto' }}>
          This could be due to a network issue, authorization problem, or the pipeline may not exist.
        </Alert>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Pipelines
        </Button>
      </Box>
    );
  }

  if (!model) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>Pipeline not found</Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Pipelines
        </Button>
      </Box>
    );
  }

  const bestScore = model.top_model?.score;
  const bestScoreColor =
    bestScore >= 80 ? theme.palette.success.main :
    bestScore >= 60 ? theme.palette.primary.main :
    bestScore >= 40 ? theme.palette.warning.main : theme.palette.error.main;

  const totalCost = Number(model.total_cost) || 0;
  const costUnit = model.total_cost_unit || 'credits';
  const scheduleConfigured = hasSchedule(model);

  // Pipeline automation surfaces (populated by the backend sweep).
  const pendingPromotion = model.pending_promotion || null;
  const budgetCap = Number(model.budget?.monthly_credit_cap) || 0;
  const budgetState = model.budget_state || {};
  const budgetPaused = !!budgetState.paused;
  const lastCycle = model.last_cycle || null;
  const cycleResults = Array.isArray(lastCycle?.results) ? lastCycle.results : [];
  const formatScore = (s) => (s === null || s === undefined ? '—' : Number(s).toFixed(2));

  return (
    <Box sx={{ p: 0 }}>
      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Pipeline</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the pipeline{model?.name ? ` "${model.name}"` : ''}?
            This action will delete all related training records and forecasts, and cannot be undone.
            {model?.in_training && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error">
                  This pipeline is currently in training and cannot be deleted.
                </Alert>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: 3 }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCloseDeleteDialog}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleConfirmDelete}
            disabled={isDeleting || model?.in_training}
          >
            {isDeleting ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}>
          <PageBreadcrumbs
            items={[
              {
                text: "Pipelines",
                icon: <PipelinesIcon />,
                link: "/pipelines"
              },
              {
                text: model.name || model.uid
              }
            ]}
            sx={{
              m: 0,
              '& .MuiTypography-root:last-child': {
                wordBreak: 'break-all',
                maxWidth: 'none',
                overflow: 'visible',
                whiteSpace: 'normal'
              }
            }}
          />

          <Box sx={{ display: 'flex' }}>
            <Button
              variant="contained"
              color={model.active ? "error" : "success"}
              startIcon={model.active ? <PauseCircleIcon /> : <PlayCircleIcon />}
              onClick={handleToggleTraining}
              disabled={isTogglingActiveState}
              sx={{ minWidth: '110px', fontWeight: 'medium', mr: 2 }}
            >
              {isTogglingActiveState
                ? <CircularProgress size={24} color="inherit" />
                : (model.active ? "Disable" : "Enable")
              }
            </Button>

            <Button
              variant="outlined"
              startIcon={
                isRunningTraining
                  ? <CircularProgress size={16} color="inherit" />
                  : <PlayArrowIcon />
              }
              sx={{ mr: 2, px: 2 }}
              size="small"
              onClick={handleRunTraining}
              disabled={
                isRunningTraining
                || !!model?.in_training
                || !model?.active
              }
            >
              Run Training
            </Button>

            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              sx={{ mr: 2, px: 2 }}
              size="small"
              onClick={() => navigate(`/pipeline/${modelId}/edit`)}
            >
              Edit
            </Button>

            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              sx={{ px: 2 }}
              size="small"
              onClick={handleOpenDeleteDialog}
            >
              Delete
            </Button>
          </Box>
        </Box>

        {actionSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionSuccess(false)}>
            {actionSuccessMessage}
          </Alert>
        )}

        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        {/* Pending promotion decision (manual promotion policy). */}
        {pendingPromotion && (
          <Alert
            severity="info"
            icon={<RocketLaunchIcon fontSize="inherit" />}
            sx={{ mb: 2, alignItems: 'center' }}
            action={(
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={promotionDeciding}
                  onClick={() => handlePromotionDecision('promote')}
                >
                  {promotionDeciding ? <CircularProgress size={16} color="inherit" /> : 'Promote & retire current'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={promotionDeciding}
                  onClick={() => handlePromotionDecision('dismiss')}
                >
                  Keep current
                </Button>
              </Stack>
            )}
          >
            {(() => {
              // The sweep's gate is "no worse than serving" (an equal
              // score on fresher data still qualifies), so say "matched"
              // when the two scores render the same instead of claiming
              // the new one "beat" it.
              const newScore = pendingPromotion.score;
              const oldScore = pendingPromotion.previous_score;
              const tied = newScore != null && oldScore != null
                && formatScore(newScore) === formatScore(oldScore);
              if (tied) {
                return (
                  <>
                    A newer training matched the serving version&apos;s score
                    ({formatScore(newScore)}) on fresher data.
                  </>
                );
              }
              return (
                <>
                  A newer training beat the serving version
                  {newScore != null && (
                    <>
                      {' '}(score {formatScore(newScore)}
                      {oldScore != null ? ` vs ${formatScore(oldScore)} serving` : ''})
                    </>
                  )}
                  .
                </>
              );
            })()}
            {' '}Promote it and retire the current deployment, or keep what&apos;s live.
          </Alert>
        )}

        {/* Budget guardrail status. */}
        {budgetPaused && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Monthly training budget reached ({formatCredits(Number(budgetState.spent) || 0)} of {formatCredits(budgetCap)} credits).
            Scheduled and data-triggered runs are paused until next month — manual &quot;Run Training&quot; still works.
          </Alert>
        )}
      </Box>

      {/* Compact Metrics + Schedule */}
      <StaticCard sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          {/* Title row: pipeline name on the left, ambient status on
              the right. Status dots stay minimal but live in the same
              card so users have a single anchor for "what is this
              pipeline / how is it doing". */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: 1.5,
          }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                wordBreak: 'break-word',
                lineHeight: 1.2,
              }}
            >
              {model.name || model.uid}
            </Typography>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
              <StatusDot
                color={model.active ? theme.palette.success.main : theme.palette.text.disabled}
                label={model.active ? 'Active' : 'Inactive'}
              />
              {model.in_training && (
                <StatusDot
                  color={theme.palette.primary.main}
                  label="Training"
                  pulsing
                />
              )}
            </Box>
          </Box>

          <Grid container spacing={2} alignItems="center">
            {/* Metrics on the left */}
            <Grid item xs={12} md={scheduleConfigured ? 6 : 12}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Best Model Score</Typography>
                  <AccuracyInfoTooltip
                    value={typeof bestScore === 'number' ? bestScore : null}
                    note="The accuracy of this pipeline's best training."
                    placement="bottom-start"
                  >
                    <Typography variant="h6" sx={{ color: bestScoreColor, fontWeight: 700, cursor: 'help' }}>
                      {bestScore !== undefined && bestScore !== null
                        ? `${bestScore.toFixed(2)}%`
                        : 'N/A'}
                    </Typography>
                  </AccuracyInfoTooltip>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Total Cost</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formatCredits(totalCost)}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                      {costUnit}
                    </Typography>
                  </Typography>
                  {budgetCap > 0 && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: budgetPaused ? theme.palette.warning.main : 'text.secondary',
                        fontWeight: budgetPaused ? 700 : 400,
                      }}
                    >
                      Budget: {formatCredits(Number(budgetState.spent) || 0)} / {formatCredits(budgetCap)} this month
                      {budgetPaused ? ' — paused' : ''}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Grid>

            {/* Schedule on the right */}
            {scheduleConfigured && (
              <>
                <Grid
                  item
                  xs={12}
                  md="auto"
                  sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'stretch' }}
                >
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                </Grid>
                <Grid item xs={12} md>
                  <ScheduleSummary model={model} />
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </StaticCard>

      {/* Latest cycle results — plain score table, best first */}
      {lastCycle && cycleResults.length >= 2 && (
        <StaticCard sx={{ mb: 3 }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Latest training cycle
              </Typography>
              {lastCycle.completed_at && (
                <Typography variant="caption" color="text.secondary">
                  · {formatDate(lastCycle.completed_at)}
                </Typography>
              )}
            </Stack>
            <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 34, pr: 0 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: { xs: '40%', md: '50%' } }}>Accuracy</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  // Rank the cycle by Accuracy (the headline metric everywhere);
                  // rows without a measured accuracy sort last and render "—".
                  const accOf = (r) => (typeof r.accuracy_score === 'number' ? r.accuracy_score : null);
                  const sorted = [...cycleResults]
                    .sort((a, b) => (accOf(b) ?? -Infinity) - (accOf(a) ?? -Infinity));
                  const maxAcc = Math.max(
                    ...sorted.map((r) => (r.status === 'completed' ? (accOf(r) ?? 0) : 0)),
                    0,
                  );
                  return sorted.map((r, idx) => {
                    const completed = r.status === 'completed';
                    const acc = completed ? accOf(r) : null;
                    const pct = maxAcc > 0 && acc != null
                      ? Math.max(0, Math.min(100, (acc / maxAcc) * 100)) : 0;
                    const best = completed && idx === 0 && acc != null && acc > 0;
                    const barColor = best ? theme.palette.success.main : theme.palette.primary.main;
                    return (
                      <TableRow key={r.training_id} sx={{ '&:last-child td': { border: 0 } }}>
                        {/* Rank badge */}
                        <TableCell sx={{ pr: 0 }}>
                          <Box
                            sx={{
                              width: 22, height: 22, borderRadius: '50%',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 11, fontWeight: 700,
                              bgcolor: best
                                ? alpha(theme.palette.success.main, 0.15)
                                : alpha(theme.palette.text.primary, 0.06),
                              color: best ? theme.palette.success.main : 'text.secondary',
                            }}
                          >
                            {idx + 1}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: best ? 700 : 400 }} noWrap>
                            {r.blueprint_name || r.blueprint_uid || '—'}
                          </Typography>
                        </TableCell>
                        {/* Score bar, scaled to the cycle's best score */}
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Box
                              sx={{
                                flex: 1, height: 8, borderRadius: 4,
                                bgcolor: alpha(theme.palette.text.primary, 0.06),
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${pct}%`, height: '100%', borderRadius: 4,
                                  bgcolor: barColor,
                                  transition: 'width 300ms ease',
                                }}
                              />
                            </Box>
                            {acc != null ? (
                              <AccuracyInfoTooltip value={acc}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontVariantNumeric: 'tabular-nums',
                                    fontWeight: best ? 700 : 400,
                                    minWidth: 56,
                                    textAlign: 'right',
                                    cursor: 'help',
                                  }}
                                >
                                  {`${acc.toFixed(1)}%`}
                                </Typography>
                              </AccuracyInfoTooltip>
                            ) : (
                              <Tooltip arrow title={completed
                                ? 'No accuracy was measured for this training (no held-out evaluation).'
                                : 'Training did not complete.'}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ minWidth: 56, textAlign: 'right', cursor: 'help' }}
                                >
                                  —
                                </Typography>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        {/* Status dot */}
                        <TableCell align="right">
                          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.75}>
                            <Box
                              sx={{
                                width: 8, height: 8, borderRadius: '50%',
                                bgcolor: completed
                                  ? theme.palette.success.main
                                  : theme.palette.error.main,
                              }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {completed ? 'Completed' : (r.status || 'failed')}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
            </Box>
          </CardContent>
        </StaticCard>
      )}

      {/* Trainings */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Trainings
        </Typography>
        <TrainingsTable modelId={modelId} formatDate={formatDate} />
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  model: state.model_detail.response,
  isLoading: state.model_detail.isLoading,
  error: state.model_detail.error,
  deleteError: state.model_delete.error,
  deleteResponse: state.model_delete.response,
  runTrainingResponse: state.model_run_training.response,
  runTrainingError: state.model_run_training.error,
  promotionDecisionResponse: state.model_promotion_decision.response,
  promotionDecisionError: state.model_promotion_decision.error,
  promotionDeciding: state.model_promotion_decision.isLoading,
});

const mapDispatchToProps = {
  getModelDetail,
  clearState,
  deleteModel: deleteModelRequest,
  deleteModelClear,
  runTraining: runTrainingRequest,
  runTrainingClear: runTrainingClearState,
  promotionDecide: promotionDecisionRequest,
  promotionDecisionClear: promotionDecisionClearState,
};

export default connect(mapStateToProps, mapDispatchToProps)(ModelDetailPage);
